<script setup lang="ts">
import { ref, computed, defineProps, watch, onMounted } from 'vue';

const props = defineProps<{ percentage: number; startColor: string; endColor: string }>();

const radius = 50;
const strokeWidth = 10;
const circumference = 2 * Math.PI * radius;
const animatedPercentage = ref(0); // Start from 0

const offset = computed(() => circumference * (1 - animatedPercentage.value / 100));
const gradientId = computed(() => `gradient-${Math.random().toString(36).substring(2, 9)}`);

watch(
  () => props.percentage,
  (newVal) => {
    animateProgress(animatedPercentage.value, newVal);
  },
  { immediate: true }
);

onMounted(() => {
  animateProgress(0, props.percentage);
});

function animateProgress(from: number, to: number) {
  const start = performance.now();
  const duration = 1000; // Animation duration in ms

  function step(timestamp: number) {
    const elapsed = timestamp - start;
    const progress = Math.min(elapsed / duration, 1); // 0 to 1
    const easedProgress = easeOutCubic(progress); // Apply easing
    animatedPercentage.value = from + (to - from) * easedProgress;

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

// Cubic ease-out function for a natural feel
function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}
</script>

<template>
  <div class="relative w-full h-full flex items-center justify-center">
    <svg class="w-full h-full" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
      <!-- Background Circle -->
      <circle
        cx="60"
        cy="60"
        :r="radius"
        :stroke-width="strokeWidth"
        class="text-foreground"
        fill="transparent"
        stroke="currentColor"
      />

      <!-- Gradient Definition -->
      <defs>
        <linearGradient :id="gradientId" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" :stop-color="startColor" />
          <stop offset="100%" :stop-color="endColor" />
        </linearGradient>
      </defs>

      <!-- Progress Circle -->
      <circle
        cx="60"
        cy="60"
        :r="radius"
        :stroke-width="strokeWidth"
        fill="transparent"
        stroke-linecap="round"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="offset"
        :stroke="`url(#${gradientId})`"
        transform="rotate(-90 60 60)"
      />
    </svg>

    <!-- Percentage Text -->
    <span class="absolute text-base font-bold text-foreground">{{ Math.round(animatedPercentage) }}%</span>
  </div>
</template>

<style scoped>
circle {
  transition: stroke-dashoffset 0.3s ease-out;
}
</style>

